import React, { useCallback } from "react"
import { StatusPill } from "./StatusPill"
import { formatFileSize } from "@ciq/shared/pages/file_upload/utils"
import { FILE_MOVE_MIME_TYPE } from "@ciq/shared/pages/file_upload/constants"

/**
 * @typedef {Object} UploadedFile
 * @property {string} id - File identifier
 * @property {string} filename - Display filename
 * @property {number} file_size_bytes - File size in bytes
 * @property {string} placement_status - Status: pending, placed, out_of_scope
 * @property {string|null} created_at - ISO timestamp of upload
 */

/**
 * Single file row with two-zone flex layout.
 * Left zone: checkbox + filename. Right zone: size, date, status pill, actions.
 * Supports drag-and-drop when dragEnabled is true.
 *
 * @param {Object} props
 * @param {UploadedFile} props.file - The uploaded file data
 * @param {boolean} props.isSelected - Whether this row's checkbox is checked
 * @param {Function} props.onSelect - Callback when checkbox toggled, receives file ID
 * @param {Function} props.onStatusChange - Callback when status changes, receives (fileId, newStatus)
 * @param {Function} props.onDownload - Callback when download clicked, receives file ID
 * @param {boolean} [props.dragEnabled=false] - Whether drag-and-drop is enabled (feature flag)
 * @param {Set<string>} [props.selectedFileIds] - Set of all selected file IDs (for multi-drag)
 * @param {boolean} [props.readOnly=false] - Boolean indicating if the row is read-only (hides checkbox, disables drag, disables status change)
 * @returns {React.ReactElement} The file row component
 */
export function FileRow({ file, isSelected, onSelect, onStatusChange, onDownload, dragEnabled = false, selectedFileIds, readOnly = false }) {
  const handleCheckbox = useCallback(() => {
    onSelect(file.id)
  }, [file.id, onSelect])

  const handleStatusChange = useCallback((newStatus) => {
    onStatusChange(file.id, newStatus)
  }, [file.id, onStatusChange])

  const handleDownload = useCallback(() => {
    onDownload(file.id)
  }, [file.id, onDownload])

  const handleDragStart = useCallback((e) => {
    const ids = isSelected && selectedFileIds ? Array.from(selectedFileIds) : [file.id]
    e.dataTransfer.setData(FILE_MOVE_MIME_TYPE, JSON.stringify(ids))
    e.dataTransfer.effectAllowed = "move"
  }, [file.id, isSelected, selectedFileIds])

  const className = `file-row${isSelected ? " selected" : ""}`

  return (
    <div
      className={className}
      data-testid={`file-row-${file.id}`}
      draggable={!readOnly && dragEnabled ? true : undefined}
      onDragStart={!readOnly && dragEnabled ? handleDragStart : undefined}
    >
      <div className="file-row-left">
        {!readOnly && (
          <input
            type="checkbox"
            className="file-checkbox"
            checked={isSelected}
            onChange={handleCheckbox}
            aria-label={`Select ${file.filename}`}
          />
        )}
        <span className="file-row-filename">{file.filename}</span>
        <span className="file-row-size">{formatFileSize(file.file_size_bytes)}</span>
        <StatusPill
          status={file.placement_status}
          onStatusChange={handleStatusChange}
          readOnly={readOnly}
        />
      </div>
      <div className="file-row-actions">
        <button
          className="file-row-action-btn"
          onClick={handleDownload}
          aria-label="Download"
          type="button"
        >
          ↓
        </button>
      </div>
    </div>
  )
}
